import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Orders,
    Products,
    Bell,
    Store
} from '../../assets/icons';
import '../../assets/css/vendor/dashboard.css';
import apiConfig from '../../config/api_config';
import LoadingSpinner from '../../components/elements/LoadingSpinner';

const VendorDashboard = () => {
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const user = JSON.parse(localStorage.getItem('user'));
    const vendorName = user?.vendor_profile?.store_name || user?.first_name || 'Vendor';

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch orders
            const ordersResponse = await apiConfig.get('/orders/');
            if (ordersResponse?.data?.data) {
                setOrders(ordersResponse.data.data);
            }
            
            // Fetch products
            const productsResponse = await apiConfig.get('/products/');
            if (productsResponse?.data?.data) {
                setProducts(productsResponse.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch dashboard data:', err);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    // Calculate stats dynamically
    const stats = useMemo(() => {
        const totalOrders = orders.length;
        const pendingOrders = orders.filter(o => o.status === 'pending').length;
        const completedOrders = orders.filter(o => o.status === 'completed').length;
        
        // Calculate revenue
        const totalRevenue = orders
            .filter(o => o.status === 'completed')
            .reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);
        
        // Monthly revenue (last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const monthlyRevenue = orders
            .filter(o => o.status === 'completed' && new Date(o.created_at) >= thirtyDaysAgo)
            .reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);
        
        // Low stock items (products with quantity < 10)
        const lowStockItems = products.filter(p => (p.stock || p.quantity || 0) < 10).length;
        const totalProducts = products.length;
        
        return {
            totalOrders,
            pendingOrders,
            completedOrders,
            totalRevenue,
            monthlyRevenue,
            lowStockItems,
            totalProducts
        };
    }, [orders, products]);

    // Get recent orders (last 5)
    const recentOrders = useMemo(() => {
        return orders.slice(0, 5).map(order => ({
            id: order.id,
            orderId: `ORD${String(order.id).padStart(3, '0')}`,
            customer: order.user?.first_name ? 
                `${order.user.first_name} ${order.user.last_name}` : 
                order.shipping_name || 'Customer',
            items: order.items?.length || 0,
            total: parseFloat(order.total) || 0,
            status: order.status,
            date: order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'
        }));
    }, [orders]);

    const [notifications] = useState([
        {
            id: 1,
            type: 'order',
            message: 'Check your pending orders',
            time: 'Now'
        }
    ]);

    if (loading) {
        return (
            <div className="vendor-dashboard">
                <div className="loading-state">
                    <LoadingSpinner />
                    <p>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="vendor-dashboard">
                <div className="error-state">
                    <p>{error}</p>
                    <button onClick={fetchDashboardData}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="vendor-dashboard">
            <header className="dashboard-header">
                <div className="welcome-section">
                    <h1>Welcome, {vendorName}</h1>
                    <p>Here's your business summary</p>
                </div>
                
                <div className="header-actions">
                    <button className="notification-btn">
                        <img src={Bell} alt="" />
                        {stats.pendingOrders > 0 && (
                            <span className="badge">{stats.pendingOrders}</span>
                        )}
                    </button>
                    <Link to="/vendor/profile" className="profile-preview">
                        <img src="/vendor-profile.jpg" alt="Vendor Profile" />
                    </Link>
                </div>
            </header>

            <div className="stats-grid">
                <div className="stat-card">
                    <h3>Total Orders</h3>
                    <div className="stat-value">{stats.totalOrders}</div>
                    <span className="stat-label">{stats.pendingOrders} pending</span>
                </div>
                <div className="stat-card">
                    <h3>Monthly Revenue</h3>
                    <div className="stat-value">₦{stats.monthlyRevenue.toLocaleString()}</div>
                    <span className="stat-label">This month</span>
                </div>
                <div className="stat-card">
                    <h3>Low Stock Alert</h3>
                    <div className="stat-value">{stats.lowStockItems}</div>
                    <span className="stat-label">items need restock</span>
                </div>
                <div className="stat-card">
                    <h3>Total Products</h3>
                    <div className="stat-value">{stats.totalProducts}</div>
                    <span className="stat-label">listed items</span>
                </div>
            </div>

            <div className="dashboard-content">
                <main className="main-section">
                    <section className="orders-section">
                        <div className="section-header">
                            <h2>Recent Orders</h2>
                            <Link to="/vendor/orders" className="view-all">
                                View All Orders
                            </Link>
                        </div>
                        
                        <div className="orders-list">
                            {recentOrders.length === 0 ? (
                                <div className="empty-state">
                                    <p>No orders yet</p>
                                </div>
                            ) : (
                                recentOrders.map(order => (
                                <div key={order.id} className="order-card">
                                    <div className="order-header">
                                        <span className="order-id">{order.orderId}</span>
                                        <span className={`status ${order.status}`}>
                                            {order.status}
                                        </span>
                                    </div>
                                    <div className="order-details">
                                        <h4>{order.customer}</h4>
                                        <p>{order.items} items</p>
                                        <span className="order-total">
                                            ₦{order.total.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="order-footer">
                                        <span>{order.date}</span>
                                        <button className="process-btn">
                                            Process Order
                                        </button>
                                    </div>
                                </div>
                            )))}
                        </div>
                    </section>

                    <section className="analytics-section">
                        <h2>Sales Analytics</h2>
                        <div className="analytics-chart">
                            {/* Add your chart component here */}
                        </div>
                    </section>
                </main>

                <aside className="dashboard-sidebar">
                    <div className="notifications-panel">
                        <h3>Recent Notifications</h3>
                        <div className="notifications-list">
                            {notifications.map(notification => (
                                <div key={notification.id} className="notification-item">
                                    <div className={`notification-icon ${notification.type}`} />
                                    <div className="notification-content">
                                        <p>{notification.message}</p>
                                        <span>{notification.time}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <nav className="quick-actions">
                        <h3>Quick Actions</h3>
                        <Link to="/vendor/products/new" className="action-btn">
                            <img src={Products} alt="" />
                            Add New Product
                        </Link>
                        <Link to="/vendor/inventory" className="action-btn">
                            <img src={Store} alt="" />
                            Update Inventory
                        </Link>
                        <Link to="/vendor/products" className="action-btn">
                            <img src={Orders} alt="" />
                            View All products
                        </Link>
                    </nav>
                </aside>
            </div>
        </div>
    );
};

export default VendorDashboard;